// Journal sticker / photo / text box handles (Jade, 2026-10-28).
//
// Rotating used to set the angle to wherever the finger pointed from the
// sticker's middle. The rotate handle sits on the top-right CORNER, not
// straight above the middle, so the first tiny move snapped the sticker round
// on its own (a wide text box jumped about 80 degrees). Now a rotation is the
// change in the finger's angle since it went down, added to the old rotation.
//
// Resizing / stretching read the finger's movement in screen directions, so on
// a tilted sticker dragging a handle "outward" could shrink it, and since a
// sticker turns around its middle, a size change slid it across the note.
// These helpers turn the finger's movement into the sticker's own directions
// and keep the opposite corner / edge where it is on screen.

const RAD = Math.PI / 180;

// Finger movement on screen -> movement along the sticker's own width (x) and
// height (y), for a sticker turned by `deg` degrees.
export function toLocalDelta(dx, dy, deg) {
  const r = -(deg || 0) * RAD, c = Math.cos(r), s = Math.sin(r);
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}

// Where the box's (unturned) top-left must go when it changes from w x h to
// nw x nh, so the point at fractions (ax, ay) of the box (0,0 = top-left,
// 1,1 = bottom-right) stays on the same spot on screen. The box turns by `deg`
// around its own middle, like CSS rotate() does.
export function anchoredTopLeft({ x, y, w, h, nw, nh, deg, ax, ay }) {
  const r = (deg || 0) * RAD, c = Math.cos(r), s = Math.sin(r);
  const ox = (ax - 0.5) * w, oy = (ay - 0.5) * h;
  const px = x + w / 2 + c * ox - s * oy;
  const py = y + h / 2 + s * ox + c * oy;
  const nx = (ax - 0.5) * nw, ny = (ay - 0.5) * nh;
  return { x: px - nw / 2 - (c * nx - s * ny), y: py - nh / 2 - (s * nx + c * ny) };
}

// Corner resize that keeps the shape: how much bigger the box gets when its
// bottom-right corner moves by (ldx, ldy) in the box's own directions. Only the
// part of the move along the box's diagonal counts, so the corner stays under
// the finger.
export function cornerScale(w, h, ldx, ldy) {
  const d = Math.hypot(w, h);
  if (!d) return 1;
  return Math.max(0, (d + (ldx * w + ldy * h) / d) / d);
}

// A rotation that follows the finger from where it went down. `startDeg` is the
// sticker's rotation then; the angles are the finger's direction from the
// sticker's middle (radians, Math.atan2). Unwrapped so going past "9 o'clock"
// doesn't flip it by a full turn.
export function makeRotation(startDeg, startAngle) {
  let last = startAngle, turned = 0;
  return (angle) => {
    let d = angle - last;
    if (d > Math.PI) d -= 2 * Math.PI;
    else if (d < -Math.PI) d += 2 * Math.PI;
    turned += d;
    last = angle;
    return (startDeg || 0) + turned / RAD;
  };
}

// Kept between -180 and 180 when saved.
export function normalizeDeg(deg) {
  const n = ((deg % 360) + 540) % 360 - 180;
  return Object.is(n, -0) ? 0 : n;
}
